import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  ShoppingBag, Euro, TrendingUp, Users, Star, Clock, BarChart3, Download,
  ArrowUpRight, ArrowDownRight, HelpCircle, Award, Gift, Heart,
  CheckCircle, XCircle, AlertTriangle, CalendarDays
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart as RechartsPieChart, Pie, Cell
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
const periodLabels = { '7d': '7 jours', '14d': '14 jours', '30d': '30 jours', '90d': '90 jours', '365d': '1 an' };

/* ─── Reusable KPI Card ─── */
const KPICard = ({ title, value, suffix, growth, icon: Icon, color, subline, tooltip, children }) => {
  const pos = growth >= 0;
  const bg = { emerald: 'bg-emerald-50 text-emerald-600', blue: 'bg-blue-50 text-blue-600', amber: 'bg-amber-50 text-amber-600', purple: 'bg-purple-50 text-purple-600' }[color] || 'bg-gray-50 text-gray-600';

  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            {tooltip && (
              <TooltipProvider delayDuration={200}>
                <ShadTooltip>
                  <TooltipTrigger asChild><HelpCircle className="w-3.5 h-3.5 text-gray-300 cursor-help" /></TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">{tooltip}</TooltipContent>
                </ShadTooltip>
              </TooltipProvider>
            )}
          </div>
          <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900">
          {typeof value === 'number' ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : value}
          {suffix && <span className="text-base ml-0.5 font-semibold text-gray-500">{suffix}</span>}
        </p>
        {growth !== undefined && (
          <div className={`flex items-center gap-1 text-xs mt-1 ${pos ? 'text-emerald-600' : 'text-red-500'}`}>
            {pos ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            <span className="font-semibold">{Math.abs(growth)}%</span>
            <span className="text-gray-400">vs periode prec.</span>
          </div>
        )}
        {subline && <p className="text-xs text-gray-400 mt-1.5">{subline}</p>}
        {children}
      </CardContent>
    </Card>
  );
};

export default function RestaurantAnalytics() {
  useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const fetch_ = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/restaurant-dashboard/analytics?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `${API}/restaurant-dashboard/analytics?start_date=${customStart}&end_date=${customEnd}`;
      }
      const r = await axios.get(url);
      setData(r.data);
    } catch {
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Date', 'Commandes', 'CA (€)'], ...data.sales_chart.map(d => [d.date, d.orders, d.revenue])];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `analytics_restaurant_${period}.csv`;
    a.click();
    toast.success('Export telecharge');
  };

  if (loading || !data) {
    return (
      <DashboardLayout title="Analytics">
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
          <div className="grid lg:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
        </div>
      </DashboardLayout>
    );
  }

  const { summary: s, clients: c, sales_chart, peak_hours, promo_hours, top_dishes, funnel, cancel_rate, reviews, promotions } = data;

  const chartFmt = (sales_chart || []).map(d => ({
    ...d, revenue: Number(d.revenue?.toFixed(2) || 0),
    label: d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''
  }));

  const funnelData = funnel ? [
    { step: 'Recues', value: funnel.received, color: '#3b82f6' },
    { step: 'Acceptees', value: funnel.accepted, color: '#8b5cf6' },
    { step: 'Preparees', value: funnel.prepared, color: '#f59e0b' },
    { step: 'Terminees', value: funnel.completed, color: '#10b981' },
  ] : [];

  const ratingStars = reviews?.distribution || [0, 0, 0, 0, 0];
  const totalRatings = ratingStars.reduce((a, b) => a + b, 0);

  return (
    <DashboardLayout title="Analytics">
      <div className="space-y-6">
        {/* ─── Header ─── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <p className="text-sm text-gray-500">Periode : {data.period?.start_date} &rarr; {data.period?.end_date}</p>
          <div className="flex gap-2 flex-wrap items-center">
            {Object.entries(periodLabels).map(([k, v]) => (
              <Button key={k} variant={period === k ? 'default' : 'outline'} size="sm" onClick={() => { setPeriod(k); setShowCustom(false); }} className={period === k ? 'bg-emerald-500 hover:bg-emerald-600' : ''} data-testid={`period-${k}`}>{v}</Button>
            ))}
            <Button variant={showCustom ? 'default' : 'outline'} size="sm" onClick={() => setShowCustom(!showCustom)} className={showCustom ? 'bg-emerald-500 hover:bg-emerald-600' : ''} data-testid="period-custom">
              <CalendarDays className="w-4 h-4 mr-1" />Personnalise
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
          </div>
        </div>
        {showCustom && (
          <div className="flex flex-wrap gap-3 items-end p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date debut</label>
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-40" data-testid="custom-start" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date fin</label>
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-40" data-testid="custom-end" />
            </div>
            <Button size="sm" onClick={() => { if (customStart && customEnd) setPeriod('custom'); else toast.error('Selectionnez les deux dates'); }} className="bg-emerald-500 hover:bg-emerald-600" data-testid="apply-custom">Appliquer</Button>
          </div>
        )}

        {/* ─── 4 KPI Cards ─── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Commandes" value={s.total_orders} growth={s.orders_growth} icon={ShoppingBag} color="emerald" tooltip="Nombre total de commandes sur la periode selectionnee." />
          <KPICard title="Chiffre d'affaires" value={s.ca_net} suffix="€" growth={s.ca_growth} icon={Euro} color="blue" tooltip="CA net = total des commandes actives (hors annulations)." />
          <KPICard title="Panier moyen" value={s.aov} suffix="€" growth={s.aov_growth} icon={TrendingUp} color="amber" subline={`Top 10% : ${s.top10_aov} €`} tooltip="AOV = CA net / nombre de commandes actives. Top 10% = panier moyen des 10% plus grosses commandes." />
          <KPICard title="Clients & fidelite" value={`${c.recurring_pct}%`} icon={Users} color="purple" subline={`${c.new_pct}% nouveaux, ${c.recurring_pct}% recurrents`} tooltip="Clients ayant deja commande chez vous avant la periode = recurrents.">
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant="outline" className="text-xs border-purple-200 bg-purple-50 text-purple-600">Repeat 90j : {c.repeat_rate_90d}%</Badge>
              {s.avg_rating > 0 && <Badge variant="outline" className="text-xs border-amber-200 bg-amber-50 text-amber-600"><Star className="w-3 h-3 mr-0.5 inline" />{s.avg_rating}/5</Badge>}
            </div>
          </KPICard>
        </div>

        {/* ─── Block 1 & 2: Sales Evolution + Peak Hours ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-5 h-5 text-emerald-500" />Evolution des ventes</CardTitle>
            </CardHeader>
            <CardContent>
              {chartFmt.length === 0 ? <p className="text-gray-400 text-center py-12">Aucune donnee sur cette periode</p> : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartFmt}>
                      <defs>
                        <linearGradient id="gCA" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.2} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `${v}€`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip formatter={(v, n) => [n === 'revenue' ? `${Number(v).toLocaleString('fr-FR')} €` : v, n === 'revenue' ? 'CA' : 'Commandes']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                      <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#gCA)" strokeWidth={2} name="revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="orders" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex justify-center gap-6 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />CA</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Commandes</span>
              </div>
            </CardContent>
          </Card>

          {/* Peak Hours */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Clock className="w-5 h-5 text-blue-500" />Heures de pointe</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(peak_hours || []).filter(h => h.hour >= 8 && h.hour <= 22)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} tickFormatter={h => `${h}h`} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip formatter={v => [v, 'Commandes']} labelFormatter={h => `${h}h00 - ${h}h59`} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }} />
                    <Bar dataKey="orders" radius={[3, 3, 0, 0]}>
                      {(peak_hours || []).filter(h => h.hour >= 8 && h.hour <= 22).map((entry, i) => (
                        <Cell key={i} fill={(promo_hours || []).includes(entry.hour) ? '#fbbf24' : '#3b82f6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {(promo_hours || []).length > 0 && (
                <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  <p className="text-[11px] text-amber-700">Heures creuses (jaune) : ideales pour activer des promos !</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Block 3 & 4: Top Dishes + Conversion Funnel ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Dishes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Award className="w-5 h-5 text-amber-500" />Top plats</CardTitle>
            </CardHeader>
            <CardContent>
              {(top_dishes || []).length === 0 ? <p className="text-gray-400 text-center py-8">Aucune donnee</p> : (
                <div className="space-y-2.5">
                  {top_dishes.map((dish, i) => (
                    <div key={dish.name || i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-sm text-gray-900 truncate">{dish.name}</p>
                          {dish.badge === 'best-seller' && <Badge className="text-[10px] py-0 bg-amber-50 text-amber-600 border-amber-200">Best-seller</Badge>}
                        </div>
                        <p className="text-xs text-gray-400">{dish.orders} vendus</p>
                      </div>
                      <p className="font-semibold text-sm text-gray-900 flex-shrink-0">{dish.revenue.toLocaleString('fr-FR')} €</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversion Funnel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="w-5 h-5 text-purple-500" />Conversion & qualite</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Funnel Bars */}
              <div className="space-y-3 mb-4">
                {funnelData.map((step, i) => {
                  const maxVal = Math.max(...funnelData.map(f => f.value), 1);
                  const pct = (step.value / maxVal) * 100;
                  return (
                    <div key={step.step}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{step.step}</span>
                        <span className="font-semibold">{step.value}</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: step.color }} />
                      </div>
                    </div>
                  );
                })}
                {funnel.cancelled > 0 && (
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-red-500">Annulees</span>
                      <span className="font-semibold text-red-500">{funnel.cancelled}</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-red-400 transition-all duration-700" style={{ width: `${(funnel.cancelled / Math.max(funnel.received, 1)) * 100}%` }} />
                    </div>
                  </div>
                )}
              </div>
              {/* Metrics */}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                <div className="text-center p-2.5 bg-red-50/60 rounded-xl">
                  <p className="text-lg font-bold text-red-600">{cancel_rate}%</p>
                  <p className="text-[10px] text-red-400">Taux d'annulation</p>
                </div>
                <div className="text-center p-2.5 bg-emerald-50/60 rounded-xl">
                  <p className="text-lg font-bold text-emerald-600">{funnel.received > 0 ? Math.round((funnel.completed / funnel.received) * 100) : 0}%</p>
                  <p className="text-[10px] text-emerald-400">Taux de completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Block 5 & 6: Promotions + Reviews ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Promotions & Loyalty */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Gift className="w-5 h-5 text-pink-500" />Promotions & fidelite</CardTitle>
            </CardHeader>
            <CardContent>
              {promotions.promo_orders === 0 ? (
                <div className="text-center py-8">
                  <Gift className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Aucune commande avec promo sur cette periode</p>
                  <p className="text-xs text-gray-300 mt-1">Activez des promotions pour attirer plus de clients !</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-pink-50 rounded-xl text-center">
                      <p className="text-xl font-bold text-pink-600">{promotions.promo_ca.toLocaleString('fr-FR')} €</p>
                      <p className="text-[10px] text-pink-400">CA genere par promos</p>
                    </div>
                    <div className="p-3 bg-pink-50 rounded-xl text-center">
                      <p className="text-xl font-bold text-pink-600">{promotions.promo_orders}</p>
                      <p className="text-[10px] text-pink-400">Commandes avec promo</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-xs text-gray-500">Panier moyen avec promo</p>
                      <p className="text-lg font-bold text-gray-900">{promotions.promo_aov} €</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Panier moyen sans promo</p>
                      <p className="text-lg font-bold text-gray-900">{promotions.non_promo_aov} €</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reviews */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><Star className="w-5 h-5 text-amber-500" />Avis clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">{reviews.avg_rating || '-'}</p>
                  <div className="flex gap-0.5 mt-1 justify-center">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(reviews.avg_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{reviews.count} avis</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {[5, 4, 3, 2, 1].map(n => {
                    const count = ratingStars[n - 1] || 0;
                    const pct = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
                    return (
                      <div key={n} className="flex items-center gap-2 text-xs">
                        <span className="w-3 text-gray-500">{n}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-6 text-right text-gray-400">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Recent reviews */}
              {(reviews.recent || []).length > 0 && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-500">Derniers avis</p>
                  {reviews.recent.map((r, i) => (
                    <div key={i} className="p-2.5 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">{r.name}</span>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star key={n} className={`w-3 h-3 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                          ))}
                        </div>
                      </div>
                      {r.comment && <p className="text-xs text-gray-500 line-clamp-2">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
              {(reviews.recent || []).length === 0 && reviews.count === 0 && (
                <p className="text-gray-400 text-center text-sm py-4">Aucun avis sur cette periode</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
